import React, { useEffect, useRef, useCallback } from "react";
import { ChatMessage, WorkflowState } from "../types/workflow.types";
import NovaMessage from "./NovaMessage";
import UserMessage from "./UserMessage";
import TypingIndicator from "./TypingIndicator";
import ChatInput from "./ChatInput";

interface ChatInterfaceProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  onFileUpload: (file: File) => void;
  onEncryptedFileUpload: (file: File) => void;
  isTyping: boolean;
  currentWorkflow: WorkflowState;
  isGenerating: boolean;
  uploadedFile: File | null;
  encryptedFile: File | null;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  onFileUpload,
  onEncryptedFileUpload,
  isTyping,
  currentWorkflow,
  isGenerating,
  uploadedFile,
  encryptedFile,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isTyping || isGenerating || messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isTyping, isGenerating, messages.length]);

  // Determine which file upload handler to use based on workflow step
  const getFileUploadHandler = useCallback(() => {
    console.log("📁 ChatInterface - Getting file upload handler:", {
      currentWorkflow,
      uploadedFile: !!uploadedFile,
      encryptedFile: !!encryptedFile,
    });

    // Check if we're at step 3 (waiting for encrypted file)
    if (
      currentWorkflow === WorkflowState.SYNKCRYPT &&
      uploadedFile &&
      !encryptedFile
    ) {
      console.log(
        "📁 ChatInterface - Using encrypted file upload handler (step 3)"
      );
      return onEncryptedFileUpload; // For encrypted file upload at step 3
    }

    // Check if we're at step 2 (waiting for first file)
    if (currentWorkflow === WorkflowState.SYNKPASS && !uploadedFile) {
      console.log(
        "📁 ChatInterface - Using initial file upload handler (step 2)"
      );
      return onFileUpload; // For initial file upload at step 2
    }

    // Default fallback
    console.log("📁 ChatInterface - Using default file upload handler");
    return onFileUpload;
  }, [
    currentWorkflow,
    uploadedFile,
    encryptedFile,
    onFileUpload,
    onEncryptedFileUpload,
  ]);

  // Debug: Log current state
  console.log("📁 ChatInterface - Current state:", {
    currentWorkflow,
    uploadedFile: !!uploadedFile,
    encryptedFile: !!encryptedFile,
    onFileUpload: !!onFileUpload,
    onEncryptedFileUpload: !!onEncryptedFileUpload,
  });

  // Fonction pour déterminer quel fichier afficher avec chaque message
  const getAttachedFileForMessage = useCallback((message: ChatMessage) => {
    return message.attachedFile || null;
  }, []);

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden">
      {/* Background shadows */}
      <div className="pointer-events-none fixed inset-0 z-[0] overflow-hidden">
        <img
          className="absolute left-[2%] top-[20%] w-[32%]"
          src="/images/shadows/1.svg"
          alt=""
        />
        <img
          className="absolute bottom-[5%] right-[-6%] w-[48%]"
          src="/images/shadows/2.svg"
          alt=""
        />
        <img
          className="absolute bottom-[0%] left-[-12%] w-[47%]"
          src="/images/shadows/3.svg"
          alt=""
        />
      </div>
      <div className="pointer-events-none fixed w-full h-full inset-0 z-[0] overflow-hidden">
        <img
          src="/images/shadows/lines.png"
          alt=""
          className="absolute -bottom-[247px] -left-[153px]"
        />
      </div>

      {/* Header with Nova title */}
      <div className="flex items-center w-full justify-center z-[900] pt-4 pb-2">
        <div
          className="select-none bg-clip-text text-center text-[3.5rem]
          select-none
          bg-clip-text text-transparent uppercase leading-none tracking-[3px] px-1 font-[700]
          bg-[radial-gradient(61.17%_178.53%_at_38.83%_-13.54%,#003330_0%,#003330_15%,#003330_35%,#003330_50%,#A8F5FF_65%,#003330_85%,#003330_100%)]"
        >
          Nova
        </div>
      </div>

      {/* Messages - Scrollable area */}
      <div className="flex-grow overflow-auto px-4 py-4 flex flex-col items-center w-full relative z-[10]">
        {messages.map((message) =>
          message.role === "user" ? (
            <UserMessage
              key={message.id}
              content={message.content}
              attachedFile={getAttachedFileForMessage(message)}
            />
          ) : (
            <NovaMessage key={message.id} content={message.content} />
          )
        )}
        {isTyping && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input - Fixed at bottom */}
      <div className="shrink-0 w-full flex justify-center relative z-30 pb-4">
        <div className="flex flex-col justify-start max-w-[800px] w-full">
          <ChatInput
            onSendMessage={onSendMessage}
            onFileUpload={getFileUploadHandler()}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
